export type EmbeddedOf<QueryFnReturnValue> = QueryFnReturnValue extends { _embedded?: infer QueryResultEmbedded }
    ? QueryResultEmbedded
    : never;
export type ItemOfEmbedded<
    QueryResultEmbedded,
    QueryResultEmbeddedKeys extends keyof QueryResultEmbedded
> = QueryResultEmbedded[QueryResultEmbeddedKeys] extends (infer I)[] ? I : never;

/**
 * The embedded collection is the type parameter the call site infers, rather than the whole query
 * function: the list endpoints resolve to `null` when the request fails, and a `(page: number)`
 * callback is not assignable to a `(...args: unknown[])` one under strictFunctionTypes.
 */
export async function getAllListItems<
    QueryResultEmbedded extends object,
    QueryResultEmbeddedKeys extends keyof QueryResultEmbedded = keyof QueryResultEmbedded
>(options: {
    queryFn: (page: number) => Promise<{ _page_count?: number; _embedded?: QueryResultEmbedded } | null>;
    listAccessor: QueryResultEmbeddedKeys;
    page?: number;
    /** A page that resolves to `null` (the list endpoints' failure value) throws instead of reading as empty. Default: off. */
    throwOnMissingPage?: boolean;
}): Promise<ItemOfEmbedded<QueryResultEmbedded, QueryResultEmbeddedKeys>[]> {
    const page = options.page || 1;
    const queryFn = options.queryFn;
    const listAccessor = options.listAccessor;
    const firstPage = await queryFn(page);
    const pageCount = firstPage?._page_count ?? 0;
    const pageRequests = [];
    if (pageCount > 1) {
        for (let nextPage = page + 1; nextPage <= pageCount; nextPage++) {
            pageRequests.push(queryFn(nextPage));
        }
    }
    const otherPages = await Promise.all(pageRequests);
    const pages = [firstPage, ...otherPages];
    if (options.throwOnMissingPage && pages.some((page) => page === null)) {
        throw new Error('A list page could not be loaded');
    }
    return pages.flatMap((page) =>
        page?._embedded && listAccessor in page._embedded
            ? (page._embedded[listAccessor] as ItemOfEmbedded<QueryResultEmbedded, QueryResultEmbeddedKeys>[])
            : []
    );
}
